<template>
  <div class="grow text-12 lg:text-14 mx-4 lg:ml-2">
    <ProviderLabels :grouped-quotes="groupedQuotes" :common-promotion="commonPromotion" />
    <div
      class="mb-2 mt-4 flex-1 items-baseline grid grid-cols-2 grid-flow-row-dense gap-2 lg:gap-3 lg:grid-cols-3"
    >
      <p class="text-gray-500 hidden lg:block">
        {{ $t('transfer_comparison.transfer_time') }}
      </p>
      <div class="text-gray-500 lg:px-2 hidden lg:flex lg:col-span-2">
        <p class="flex-1">{{ $t('transfer_comparison.fee_rates') }}</p>
        <p class="flex-1 text-right">
          {{ $t('transfer_comparison.recipient_gets') }}
        </p>
      </div>
      <template v-for="(service, i) in services" :key="service.amount">
        <hr
          v-if="i !== 0"
          class="border-t-2 border-gray-100 col-span-2 pt-2 mt-2 lg:pt-0 lg:mt-0 lg:col-span-3"
        />
        <div class="flex-1">
          <p class="font-semibold text-16 lg:text-18">
            {{
              $t(`transfer_time.${service.transferTime.key}`, {
                count: service.transferTime.min,
                min: service.transferTime.min,
                max: service.transferTime.max,
              })
            }}
          </p>
          <p class="hidden lg:block">
            {{ $t('transfer_comparison.pay_by') }}
            {{ service.payins.map((p) => $t(`payin.${p}`)).join(', ') }}
          </p>
        </div>
        <p class="text-right lg:hidden text-gray-500">
          {{ $t('transfer_comparison.recipient_gets') }}
        </p>
        <ProviderService
          class="col-span-2"
          :provider="groupedQuotes.psp"
          :service="service"
          :has-common-promo="commonPromotion !== null"
          :mid-market-rate="midMarketRate"
        />
      </template>
    </div>
    <div class="hidden lg:block xl:mb-4" v-if="groupedQuotes.services.length > services.length">
      <button class="link dark font-normal" type="button" @click="clickShowMore">
        + {{ $t('transfer_comparison.more_payment_options') }}
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { GroupedQuotes, Promotion } from './types'
import ProviderLabels from './provider-labels.vue'
import ProviderService from './provider-service.vue'
import { panelOpen } from './analytics/actions'
import { Comparison } from './analytics/objects/Comparison'
import { ComparisonSection } from './analytics/objects/ComparisonSection'
import { ComparisonResult } from './analytics/objects/ComparisonResult'

const props = defineProps<{
  groupedQuotes: GroupedQuotes
  midMarketRate: number
  commonPromotion: Promotion | null
}>()

const { $track } = useNuxtApp()

const getResultAnalyticsObj = inject('getResultAnalyticsObj') as (
  slug: string,
  location?: string,
) => ComparisonResult | undefined
const sectionAnalyticsObj = inject<Ref<ComparisonSection>>('sectionAnalyticsObj')
const comparisonAnalyticsObj = inject<Ref<Comparison>>('comparisonAnalyticsObj')

const showAllServices = ref(false)

async function clickShowMore() {
  showAllServices.value = true
  await panelOpen(
    $track,
    'Additional Services',
    comparisonAnalyticsObj?.value,
    sectionAnalyticsObj?.value,
    getResultAnalyticsObj(props.groupedQuotes.psp.slug),
  )
}

const services = computed(() => {
  if (showAllServices.value) {
    return props.groupedQuotes.services
  }

  let bestAmount = 0
  let cheapestIndex = 0
  let bestTime = Number.MAX_SAFE_INTEGER
  let fastestIndex = 0
  props.groupedQuotes.services.forEach(({ transferTime, amount }, index) => {
    if (amount > bestAmount) {
      bestAmount = amount
      cheapestIndex = index
    }
    const time = (transferTime.min + transferTime.max) / 2
    if (time < bestTime) {
      bestTime = time
      fastestIndex = index
    }
  })
  const cheapest = props.groupedQuotes.services[cheapestIndex]
  const fastest = props.groupedQuotes.services[fastestIndex]
  if (cheapestIndex === fastestIndex) {
    return [cheapest]
  }
  return fastestIndex > cheapestIndex ? [cheapest, fastest] : [fastest, cheapest]
})
</script>
